import React, { useState, useEffect, useRef } from 'react';
import { 
  FormControl, 
  TextField, 
  Autocomplete, 
  CircularProgress, 
  Box, 
  Typography, 
  FormHelperText, 
  IconButton, 
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Divider
} from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import ConfirmationModal from '../ConfirmationModal';
import ClientModal from '../ClientModal';
import { CLIENT_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { getClientPartyTypeName, getClientPartyTypeBackgroundColor, getClientPartyTypeTextColor } from '../../../constants/clientPartyTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = CLIENT_RETURN_TYPES;

const ClientSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  label = "Client",
  required = false,
  disabled = false,
  fullWidth = true,
  autoFocus = false,
  returnValue = CLIENT_RETURN_TYPES.ID, // CLIENT_RETURN_TYPES.ID or CLIENT_RETURN_TYPES.NAME
  onOpen,
  onClose,
  onCreateNewClient, // Callback for when a new client should be created
  enableClientCreation = true, // Whether to enable client creation functionality
  refreshTrigger, // When this value changes, clients will be refetched
  size = 'medium',
  ...props 
}) => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingClientName, setPendingClientName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [clientModal, setClientModal] = useState({ open: false, initialName: '' });
  const [isAboutToCreateClient, setIsAboutToCreateClient] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { showAlert } = useAlert();
  const inputRef = useRef(null);
  const prevValueRef = useRef(value);

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      const clientsData = await organizationApi.getClients();
      setClients(clientsData || []);
    } catch (error) {
      showAlert('Failed to load clients', 'error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (_, newValue) => {
    if (multiple) {
      // Handle multiple selection
      if (onChange) {
        if (returnValue === CLIENT_RETURN_TYPES.NAME) {
          const selectedNames = newValue.map(client => client?.name || '').filter(Boolean);
          onChange(selectedNames);
        } else {
          const selectedIds = newValue.map(client => client?.id || '').filter(Boolean);
          onChange(selectedIds);
        }
      }
    } else {
      // Handle single selection (existing logic)
      if (typeof newValue === 'string') {
        // User typed a new client name
        const existingClient = clients.find(client => 
          client.name.toLowerCase() === newValue.toLowerCase()
        );
        
        if (!existingClient && newValue.trim()) {
          if (onCreateNewClient) {
            // Call the callback to handle client creation externally (legacy support)
            onCreateNewClient(newValue.trim());
          } else if (enableClientCreation) {
            // Open ClientModal for creating new client
            setClientModal({
              open: true,
              initialName: newValue.trim()
            });
          }
          return;
        }
      }
      
      // Handle selection or clearing
      if (onChange) {
        if (newValue === null) {
          // Clearing the selection
          onChange('');
        } else if (returnValue === CLIENT_RETURN_TYPES.NAME) {
          onChange(newValue?.name || '');
        } else {
          onChange(newValue?.id || '');
        }
      }
    }
  };

  const handleCreateNewClient = (event) => {
    // Prevent any event bubbling that might trigger form submission
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const trimmedInput = inputValue.trim();
    if (trimmedInput) {
      // Set flag to prevent onClose from being triggered
      setIsAboutToCreateClient(true);
      
      if (onCreateNewClient) {
        // Call the callback to handle client creation externally (legacy support)
        onCreateNewClient(trimmedInput);
      } else if (enableClientCreation) {
        // Open ClientModal for creating new client
        setClientModal({
          open: true,
          initialName: trimmedInput
        });
      }
    }
  };

  const handleCreateClient = async () => {
    try {
      setIsCreating(true);
      const newClient = await organizationApi.createClient({ 
        name: pendingClientName 
      });
      
      // Add the new client to the clients list
      setClients(prev => [...prev, newClient]);
      
      // Select the newly created client
      if (onChange) {
        if (returnValue === CLIENT_RETURN_TYPES.NAME) {
          onChange(newClient.name);
        } else {
          onChange(newClient.id);
        }
      }
      
      // Update input value to show the created client
      setInputValue(newClient.name);
      
      showAlert(`Client "${newClient.name}" created successfully`, 'success');
      setShowConfirmModal(false);
      setPendingClientName('');
    } catch (error) {
      showAlert(`Failed to create client: ${error.message}`, 'error', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowConfirmModal(false);
    setPendingClientName('');
    setInputValue('');
  };

  const handleClientCreated = (newClient) => {
    // Add the new client to the clients list
    setClients(prev => [...prev, newClient]);
    
    // Select the newly created client
    if (onChange) {
      if (returnValue === CLIENT_RETURN_TYPES.NAME) {
        onChange(newClient.name);
      } else {
        onChange(newClient.id);
      }
    }
    
    // Update input value to show the created client
    setInputValue(newClient.name);
    
    // Reset the flag
    setIsAboutToCreateClient(false);
    
    showAlert(`Client "${newClient.name}" created successfully`, 'success');
  };

  const handleCloseClientModal = () => {
    setClientModal({
      open: false,
      initialName: ''
    });
    
    // Reset the flag when modal closes
    setIsAboutToCreateClient(false);
  };

  // Find the selected client(s) object from the ID or name
  const selectedClients = (() => {
    if (multiple) {
      if (!Array.isArray(value) || value.length === 0) return [];
      
      return value.map(val => {
        return clients.find(client => {
          if (returnValue === CLIENT_RETURN_TYPES.NAME) {
            return client.name === val;
          } else {
            return client.id === val;
          }
        });
      }).filter(Boolean);
    } else {
      const selectedClient = clients.find(client => {
        if (returnValue === CLIENT_RETURN_TYPES.NAME) {
          return client.name === value;
        } else {
          return client.id === value;
        }
      }) || null;
      return selectedClient;
    }
  })();
  
  // Get the single selected client for single mode
  const selectedClient = multiple ? null : selectedClients;
  
  // Check if input value would show any options in the dropdown
  const filteredOptionsCount = inputValue ? clients.filter(client =>
    client.name.toLowerCase().includes(inputValue.toLowerCase())
  ).length : 0;
  const isNewClient = inputValue && filteredOptionsCount === 0 && !selectedClient;
  
  // Sync input value with selected client
  useEffect(() => {
    if (selectedClient && inputValue !== selectedClient.name) {
      setInputValue(selectedClient.name);
    }
  }, [selectedClient, inputValue]);

  // Clear input only when value transitions from something to empty
  useEffect(() => {
    const prevValue = prevValueRef.current;
    const currentValue = value;
    
    // Only clear input if value changed from something to empty
    if (prevValue && !currentValue) {
      setInputValue('');
    }
    
    // Update the ref for next time
    prevValueRef.current = currentValue;
  }, [value]);

  // Handle auto-focus for DataGrid edit cells
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const focusInput = () => {
        const input = inputRef.current?.querySelector('input');
        if (input && document.contains(input)) {
          input.focus();
          // Don't select all text, just position cursor at end
          input.setSelectionRange(input.value.length, input.value.length);
          // Auto-open the dropdown after focusing
          setTimeout(() => {
            const popupButton = inputRef.current?.querySelector('.MuiAutocomplete-popupIndicator');
            if (popupButton) {
              popupButton.click();
            }
          }, 100);
          return true; // Successfully focused
        }
        return false; // Focus failed
      };
      
      // Try immediate focus first
      if (focusInput()) {
        return; // Success on first try
      }
      
      // Use requestAnimationFrame for better timing with DOM updates
      let rafId = null;
      let attempts = 0;
      const maxAttempts = 5; // Limit attempts to prevent infinite loops
      
      const tryFocusWithRAF = () => {
        if (attempts < maxAttempts && !focusInput()) {
          attempts++;
          rafId = requestAnimationFrame(tryFocusWithRAF);
        }
      };
      
      rafId = requestAnimationFrame(tryFocusWithRAF);
      
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }
  }, [autoFocus]);

  if (multiple) {
    const filteredClients = clients.filter(client => 
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderValue = (selected) => {
      if (!selected || selected.length === 0) {
        return <em>None</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {
            const client = clients.find(c => c.id === value);
            return (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip 
                  label={client?.name} 
                  size="small"
                />
                {client?.partyType && (
                  <Chip
                    label={getClientPartyTypeName(client.partyType)}
                    size="small"
                    sx={{
                      height: '16px',
                      fontSize: '10px',
                      fontWeight: 500,
                      backgroundColor: getClientPartyTypeBackgroundColor(client.partyType),
                      color: getClientPartyTypeTextColor(client.partyType),
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      );
    };

    return (
      <FormControl fullWidth={fullWidth} disabled={disabled || isLoading} size={size} {...props}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          value={Array.isArray(value) ? value : []}
          onChange={(e) => handleChange(null, clients.filter(c => e.target.value.includes(c.id)))}
          label={label}
          input={<OutlinedInput label={label} />}
          renderValue={renderValue}
          endAdornment={isLoading ? <CircularProgress size={20} /> : null}
        >
          <MenuItem sx={{ p: 0 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  }
                }
              }}
            />
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          {filteredClients.map(client => (
            <MenuItem key={client.id} value={client.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <span>{client.name}</span>
                {client.partyType && (
                  <Chip
                    label={getClientPartyTypeName(client.partyType)}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '10px',
                      fontWeight: 500,
                      backgroundColor: getClientPartyTypeBackgroundColor(client.partyType),
                      color: getClientPartyTypeTextColor(client.partyType),
                      marginLeft: 'auto',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <>
      <FormControl fullWidth={fullWidth} {...props}>
        <Autocomplete
        options={clients}
        loading={isLoading}
        disabled={disabled || isLoading}
        getOptionLabel={(option) => option?.name || ''}
        value={selectedClients}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        onOpen={onOpen}
        onClose={(event, reason) => {
          // Don't trigger onClose if we're about to create a client
          if (isAboutToCreateClient) {
            return;
          }
          onClose?.(event, reason);
        }}
        freeSolo
        openOnFocus
        selectOnFocus={false}
        clearOnBlur={false}
        handleHomeEndKeys
        filterOptions={(options, params) => {
          // If no input value and dropdown is opening, show all options
          if (!params.inputValue.trim()) {
            return options;
          }
          // Otherwise use default filtering
          return options.filter(option => 
            option.name.toLowerCase().includes(params.inputValue.toLowerCase())
          );
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <span>{option.name}</span>
              {option.partyType && (
                <Chip
                  label={getClientPartyTypeName(option.partyType)}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '10px',
                    fontWeight: 500,
                    backgroundColor: getClientPartyTypeBackgroundColor(option.partyType),
                    color: getClientPartyTypeTextColor(option.partyType),
                    marginLeft: 'auto',
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={inputRef}
            label={label}
            required={required}
            autoFocus={autoFocus}
            placeholder="Search clients..."
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const trimmedInput = inputValue.trim();
                // If there's input text and it would create a new client, prevent default behavior
                if (trimmedInput && isNewClient) {
                  event.preventDefault();
                  event.stopPropagation();
                  // Don't clear the input, keep it for the user to see the + button
                  return false;
                }
              }
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {isNewClient && (onCreateNewClient || enableClientCreation) && (
                      <Tooltip title={`Create new client "${inputValue}"`}>
                        <IconButton
                          type="button"
                          size="small"
                          onClick={handleCreateNewClient}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          sx={{
                            color: '#2563eb',
                            padding: '4px',
                            marginRight: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(37, 99, 235, 0.1)'
                            }
                          }}
                        >
                          <FaPlus size={12} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#E2E8F0',
                },
                '&:hover fieldset': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563eb',
                }
              }
            }}
          />
        )}
        noOptionsText={
          isLoading ? "Loading..." : (
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No clients found.
              </Typography>
              {inputValue && (onCreateNewClient || enableClientCreation) && (
                <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                  Click the + button to create "{inputValue}"
                </Typography>
              )}
            </Box>
          )
        }
      />
      
      {isNewClient && (onCreateNewClient || enableClientCreation) && (
        <FormHelperText sx={{ color: '#2563eb', fontWeight: 500, mt: 0.5 }}>
          Click the + button to create "{inputValue}" as a new client
        </FormHelperText>
      )}
      
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelCreate}
        onConfirm={handleCreateClient}
        title="Create New Client"
        message={`Do you want to create "${pendingClientName}" as a new client?`}
        actionButtonText={isCreating ? "Creating..." : "Create"}
      />
    </FormControl>
    
    <ClientModal
      isOpen={clientModal.open}
      onClose={handleCloseClientModal}
      onClientCreated={handleClientCreated}
      initialData={{ name: clientModal.initialName }}
    />
  </>
  );
};

export default ClientSelector; 